import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Avatar,
    Chip,
    Button,
    Stack,
    IconButton,
    Divider,
    List,
    ListItemAvatar,
    ListItemText,
    ListItemButton,
    Paper,
} from '@mui/material';
import {
    Insights as InsightsIcon,
    VpnKey as KeyIcon,
    ReportProblem as AlertIcon,
    CalendarMonth as CalendarIcon,
    Add as AddIcon,
    CheckCircle as CheckIcon,
    AccessTime as ClockIcon,
    Build as WrenchIcon,
} from '@mui/icons-material';

import mewpService from '../../services/mewpService';
import { formatToIST } from '../../utils/dataUtils';
import { useDeviceStore } from '../../store/DeviceStore';
import { resolveDeviceStatus, statusHex, statusLabel } from '../../utils/liveStatus';
import LiveStatusBadge from '../ui/LiveStatusBadge';

// Fix Leaflet marker icons
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});
L.Marker.prototype.options.icon = DefaultIcon;

// A coloured dot marker keyed to the live status colour.
const statusDivIcon = (hex) => L.divIcon({
    className: 'live-status-marker',
    html: `<div style="width:18px;height:18px;border-radius:50%;background:${hex};border:3px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.25),0 2px 6px rgba(0,0,0,.4)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -10],
});

const MEWPMonitoringPage = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalFleet: 0,
        activeRentals: 0,
        maintenanceOverdue: 0,
        upcomingServices: 0,
        utilizationRate: 0
    });
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);

    // Live status comes from the global DeviceStore (Socket.IO + 10s poll). We
    // overlay it onto this page's device list by matching device_id, so status
    // updates in real time while the rest of the row stays from getMonitoringData.
    const { state: deviceState } = useDeviceStore();
    const liveMap = useMemo(() => {
        const m = new Map();
        (deviceState?.devices || []).forEach(d => m.set(String(d.device_id), d));
        return m;
    }, [deviceState?.devices]);
    const liveOf = (device) => liveMap.get(String(device.device_id)) || device;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [statsRes, devicesRes] = await Promise.all([
                    mewpService.getDashboardStats(),
                    mewpService.getMonitoringData()
                ]);

                if (statsRes) {
                    setStats({
                        totalFleet: statsRes.totalFleet || 0,
                        activeRentals: statsRes.activeRentals || 0,
                        maintenanceOverdue: statsRes.maintenanceOverdue || 0,
                        upcomingServices: statsRes.upcomingServices || 0,
                        utilizationRate: statsRes.utilizationRate || 0
                    });
                }

                const list = devicesRes?.devices || devicesRes?.data || (Array.isArray(devicesRes) ? devicesRes : []);
                setDevices(Array.isArray(list) ? list : []);
                setLoading(false);
            } catch (error) {
                console.error("Failed to fetch MEWP data", error);
                setDevices([]);
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const serviceHistory = [
        { id: 1, date: 'Feb 04, 2024', ago: '3 mo ago', title: 'Annual Service', hours: '3500h', dist: '200KM' },
        { id: 2, date: 'Apr 05, 2023', ago: '1 yr ago', title: 'Annual Service', hours: '2240h', dist: '120KM' },
    ];

    const upcomingServices = [
        { id: 1, name: 'Scissors Lift 05', task: 'Annual Safety Inspection', date: 'Tomorrow', priority: 'Critical' },
        { id: 2, name: 'Articulated Boom 22', task: 'Battery System Check', date: 'May 18', priority: 'Routine' },
    ];

    return (
        <Box p={3} className="animate-fade-in">
            {/* Actions — no page title, the top bar already names the page */}
            <Box display="flex" justifyContent="flex-end" mb={3}>
                <Button variant="contained" startIcon={<AddIcon />} disableElevation className="gradient-primary" sx={{ px: 4, borderRadius: '14px' }}>
                    Add Asset
                </Button>
            </Box>

            {/* Stats Grid */}
            <Grid container spacing={3} mb={5}>
                {[
                    { label: 'Total Fleet', value: stats.totalFleet, sub: 'Registered Units', icon: <InsightsIcon />, color: '#2563eb' },
                    { label: 'Active Rentals', value: stats.activeRentals, sub: `${stats.utilizationRate}% Utilization`, icon: <KeyIcon />, color: '#10b981' },
                    { label: 'Mtce Overdue', value: stats.maintenanceOverdue, sub: 'Action required', icon: <AlertIcon />, color: '#ef4444' },
                    { label: 'Upc Services', value: stats.upcomingServices, sub: 'Next 7 days', icon: <CalendarIcon />, color: '#f59e0b' }
                ].map((stat, i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                        <Card sx={{ height: '100%', borderBottom: `4px solid ${stat.color}` }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Avatar sx={{ bgcolor: `${stat.color}15`, color: stat.color, borderRadius: '12px' }}>{stat.icon}</Avatar>
                                    <Typography variant="h4" sx={{ fontWeight: 800 }}>{stat.value}</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.6, textTransform: 'uppercase' }}>{stat.label}</Typography>
                                <Typography variant="caption" sx={{ color: stat.color === '#ef4444' ? 'error.main' : 'text.secondary', fontWeight: 600 }}>{stat.sub}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Map & List Grid */}
            <Grid container spacing={3} mb={5}>
                <Grid item xs={12} lg={4}>
                    <Card sx={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
                        <CardHeader title="Device Status" titleTypographyProps={{ fontWeight: 700 }} />
                        <Divider />
                        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                            <List disablePadding>
                                {devices.map((device, i) => (
                                    <React.Fragment key={device.device_id || i}>
                                        <ListItemButton
                                            onClick={() => navigate(`/mewp-detail/${device.device_id}`)}
                                            sx={{ '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.04)' } }}
                                        >
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: 'primary.50', color: 'primary.main', borderRadius: '8px' }}><InsightsIcon fontSize="small" /></Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{device.name}</Typography>}
                                                secondary={<Typography variant="caption" color="text.secondary">{device.device_id}</Typography>}
                                            />
                                            <Stack alignItems="flex-end" spacing={0.5}>
                                                <LiveStatusBadge device={liveOf(device)} size="small" />
                                                <Typography variant="caption" sx={{ fontSize: '10px' }}>{device.type}</Typography>
                                            </Stack>
                                        </ListItemButton>
                                        <Divider component="li" />
                                    </React.Fragment>
                                ))}
                            </List>
                        </Box>
                    </Card>
                </Grid>

                <Grid item xs={12} lg={8}>
                    <Card sx={{ height: '600px', borderRadius: '24px', overflow: 'hidden', border: 'none', boxShadow: 'var(--shadow-lg)' }}>
                        <MapContainer
                            center={devices.find(d => d.location?.lat)?.location ? [parseFloat(devices.find(d => d.location?.lat).location.lat), parseFloat(devices.find(d => d.location?.lat).location.lon)] : [20.5937, 78.9629]}
                            zoom={devices.find(d => d.location?.lat) ? 12 : 5}
                            style={{ height: '100%', width: '100%' }}
                        >
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM' />
                            {devices.map((device) => {
                                const live = liveOf(device);
                                const lat = parseFloat(live.location?.lat ?? live.lat ?? device.location?.lat);
                                const lon = parseFloat(live.location?.lon ?? live.lng ?? device.location?.lon);
                                if (!isNaN(lat) && !isNaN(lon) && lat !== 0) {
                                    const code = resolveDeviceStatus(live);
                                    return (
                                        <Marker key={device.device_id} position={[lat, lon]} icon={statusDivIcon(statusHex(code))}>
                                            <Popup>
                                                <Box p={1}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{device.name}</Typography>
                                                    <Typography variant="caption">{device.brand} • {device.type}</Typography>
                                                    <Box mt={1} mb={1}>
                                                        <Chip label={statusLabel(code)} size="small"
                                                            sx={{ bgcolor: statusHex(code), color: '#fff', fontWeight: 700 }} />
                                                    </Box>
                                                    <Box display="flex" gap={1}>
                                                        <Chip label={`${device.payload?.fuel_level || 0}% Fuel`} size="small" />
                                                        <Chip label={`${device.payload?.engine_working_hours || 0}h`} size="small" />
                                                    </Box>
                                                </Box>
                                            </Popup>
                                        </Marker>
                                    );
                                }
                                return null;
                            })}
                        </MapContainer>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default MEWPMonitoringPage;
